import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useWallet } from '../context/WalletContext';
import { WalletIcon, ArrowRightIcon } from '@heroicons/react/24/outline';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isConnected, isConnecting, connectWallet } = useWallet();
  const location = useLocation();

  // Mientras se está conectando, mostrar loading
  if (isConnecting) {
    return (
      <div className="min-h-screen bg-dark-950 pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Conectando wallet...</p>
        </div>
      </div>
    );
  }

  // Si no está conectado, mostrar página de conexión
  if (!isConnected) {
    return (
      <div className="min-h-screen bg-dark-950 pt-20">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-md mx-auto text-center">
            <div className="w-20 h-20 bg-primary-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <WalletIcon className="w-10 h-10 text-primary-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-4">
              Conexión Requerida
            </h1>
            <p className="text-gray-400 mb-8">
              Para acceder a tu dashboard y participar en subastas, necesitas conectar tu wallet.
            </p>
            <button
              onClick={connectWallet}
              className="btn-primary inline-flex items-center gap-2"
            >
              <WalletIcon className="w-5 h-5" />
              Conectar Wallet
            </button>
            <p className="mt-6 text-sm text-gray-500">
              ¿Nuevo en crypto?{' '}
              <a href="/how-it-works" className="text-primary-400 hover:text-primary-300">
                Aprende cómo funciona
              </a>
            </p>
          </div>

          {/* Beneficios */}
          <div className="max-w-2xl mx-auto mt-16">
            <h2 className="text-xl font-semibold text-white text-center mb-8">
              ¿Por qué conectar tu wallet?
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700 text-center">
                <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ArrowRightIcon className="w-6 h-6 text-green-400" />
                </div>
                <h3 className="text-white font-medium mb-2">Participa</h3>
                <p className="text-gray-400 text-sm">Haz pujas en subastas y compra NFTs</p>
              </div>
              <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700 text-center">
                <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ArrowRightIcon className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="text-white font-medium mb-2">Stakea</h3>
                <p className="text-gray-400 text-sm">Obtén descuentos y beneficios exclusivos</p>
              </div>
              <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700 text-center">
                <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ArrowRightIcon className="w-6 h-6 text-purple-400" />
                </div>
                <h3 className="text-white font-medium mb-2">Gobierna</h3>
                <p className="text-gray-400 text-sm">Vota en propuestas del DAO</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Si está conectado, renderizar los children
  return <>{children}</>;
};

export default ProtectedRoute;
